import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';
import { TabControlComponent } from '../../../shared/controls/tab-control/tab-control.component';
import { TabPageComponent } from '../../../shared/controls/tab-control/tab-page.component';
import { ReportParameter } from '../../../shared/models/report-parameter.model';
import { ReportConstants } from '../../../shared/constants/report.constants';

import { ErrorService } from '../../../shared/services/error-service';
import { ToastService } from '../../../shared/toast/toast.service';

import { AllPeriodItem, ClientAreaService, ClientService, PeriodService } from '../../../../service/proxy';

@Component({
    selector: 'app-data-status',
    templateUrl: './data-status.component.html',
    styleUrl: './data-status.component.scss',

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent,
        TabControlComponent,
        TabPageComponent,
    ],

    providers: [
        PeriodService,
        ClientService,
        ClientAreaService
    ]
})
export class DataStatusReportParametersComponent implements OnInit {
    public isSaving: boolean = false;
    public formSubmitted: boolean = false;

    public periods: AllPeriodItem[] = [];
    public selectedPeriodId: number = 0;

    public clients: { id: number; name: string; }[] = [];
    public selectedClientId: number = 0;

    public clientAreas: { id: number; name: string; }[] = [];
    public selectedClientAreaId: number = 0;

    constructor(
        private router: Router,
        private errorService: ErrorService,
        private toastService: ToastService,
        private periodService: PeriodService,
        private clientService: ClientService,
        private clientAreaService: ClientAreaService
    ) {}

    ngOnInit(): void {
        this.getPeriodsForLookup();
        this.getClientsForLookup();
    }

    getPeriodsForLookup(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.periods = data.results || [];
            });
    }

    getClientsForLookup(): void {
        this.clientService.getAllClients()
            .subscribe({
                next: (response) => {
                    this.clients = response.results.map(client => ({
                        id: client.id,
                        name: client.clientName
                    }));
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            });
    }

    onPeriodSelectionChange(selectedId: number | null): void {
        this.selectedPeriodId = selectedId ?? 0;
    }

    onClientSelectionChange(selectedId: number | null): void {
        this.selectedClientId = selectedId ?? 0;
        this.selectedClientAreaId = 0;
        this.getClientAreasForLookup();
    }

    onClientAreaSelectionChange(selectedId: number | null): void {
        this.selectedClientAreaId = selectedId ?? 0;
    }

    getClientAreasForLookup(): void {
        if (!this.selectedClientId) {
            this.clientAreas = [];
            return;
        }

        this.clientAreaService.getClientAreaLookup(this.selectedClientId)
            .subscribe({
                next: (clientAreas) => {
                    if (!clientAreas.length) {
                        this.toastService.show('No policies found for the selected client', 'info');
                        return;
                    }

                    this.clientAreas = clientAreas.map(area => ({
                        id: area.id,
                        name: area.name
                    }));
                },
                error: () => {
                    this.toastService.show('Failed to load client areas', 'error');
                }
            });
    }

    displayPeriod(option: any): string {
        const paddedMonth = ('0' + option.month).slice(-2);
        return `${option.year}-${paddedMonth}`;
    }

    onRunReport(form: any): void {
        this.formSubmitted = true;

        if (!form.valid || !this.selectedPeriodId || !this.selectedClientId || !this.selectedClientAreaId) {
            return;
        }

        const selectedPeriod = this.periods.find(p => p.id === this.selectedPeriodId);
        const periodValue = selectedPeriod?.month && selectedPeriod.month < 10
            ? `${selectedPeriod.year}-0${selectedPeriod.month}`
            : `${selectedPeriod?.year}-${selectedPeriod?.month}`;

        const selectedClientValue = this.clients.find(c => c.id === this.selectedClientId);
        const clientValue = selectedClientValue?.name || '';

        const selectedClientArea = this.clientAreas.find(c => c.id === this.selectedClientAreaId);
        const clientAreaValue = selectedClientArea?.name || '';

        const reportParams = [
            new ReportParameter(0, 'Client Area', this.selectedClientAreaId, clientAreaValue),
            new ReportParameter(1, 'Period', this.selectedPeriodId, periodValue),
            new ReportParameter(2, 'Client', this.selectedClientId, clientValue),
        ];

        this.router.navigate(['/report-viewer', ReportConstants.DataStatusReport], {
            state: { reportParams }
        });
    }
}